// ============================================================
// UNREAD COMMENT TRACKING (localStorage - single key, limited entries)
// ============================================================

const VISIT_STORAGE_KEY = 'msstavby_visits';
const MAX_TRACKED_POSTS = 50; // Limit to prevent storage overflow

// Get visit data from localStorage
function getVisitData() {
  try {
    const stored = localStorage.getItem(VISIT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
}

// Save visit data to localStorage (with FIFO limit)
function saveVisitData(data) {
  const keys = Object.keys(data);
  // If over limit, remove oldest entries
  if (keys.length > MAX_TRACKED_POSTS) {
    // Sort by timestamp and keep only the most recent
    const sorted = keys.sort((a, b) => data[b] - data[a]);
    const toKeep = sorted.slice(0, MAX_TRACKED_POSTS);
    const newData = {};
    toKeep.forEach(k => newData[k] = data[k]);
    data = newData;
  }
  try {
    localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    // Storage full - clear old data and try again
    localStorage.removeItem(VISIT_STORAGE_KEY);
  }
}

// Record visit to a post
function recordPostVisit(postId) {
  const data = getVisitData();
  data[postId] = Date.now();
  saveVisitData(data);
}

// Get last visit timestamp for a post
function getPostVisitTime(postId) {
  const data = getVisitData();
  return data[postId] || null;
}

// Initialize on single post pages
function initPostVisitTracking() {
  const body = document.body;
  if (body.classList.contains('single-post')) {
    const match = body.className.match(/postid-(\d+)/);
    if (match && match[1]) {
      recordPostVisit(match[1]);
    }
  }
}

// Indicate unread comments on single post pages
function indicateUnreadComments() {
  const commentList = document.querySelector('.comment-list');
  if (!commentList) return;

  const body = document.body;
  const postIdMatch = body.className.match(/postid-(\d+)/);
  if (!postIdMatch) return;

  const currentPostId = postIdMatch[1];
  const lastVisit = getPostVisitTime(currentPostId);

  commentList.querySelectorAll('li[id^="comment-"]').forEach(li => {
    const commentTimeAttr = li.getAttribute('data-comment-time');
    if (!commentTimeAttr) return;

    const commentTimestamp = parseInt(commentTimeAttr, 10) * 1000;

    // Unread if: first visit OR comment newer than last visit
    if (!lastVisit || commentTimestamp > lastVisit) {
      li.classList.add('unread');
    }
  });
}

// Indicate unread comments on homepage latest comments section
function indicateUnreadLatestComments() {
  const grid = document.querySelector('.latest-comments-section .comments-grid');
  if (!grid) return;

  grid.querySelectorAll('[data-comment-post-id]').forEach(card => {
    const postId = card.getAttribute('data-comment-post-id');
    const commentTime = card.getAttribute('data-comment-time');
    if (!postId || !commentTime) return;

    const lastVisit = getPostVisitTime(postId);
    const commentTimestamp = parseInt(commentTime, 10) * 1000;

    // Unread if: never visited OR comment newer than last visit
    if (!lastVisit || commentTimestamp > lastVisit) {
      card.classList.add('unread-comment-card');
    }
  });
}

// Indicate unread comments on comments page (page-comments.php)
function indicateUnreadCommentsPage() {
  const pageRoot = document.querySelector('.page-template-page-comments');
  if (!pageRoot) return;

  pageRoot.querySelectorAll('[data-comment-post-id]').forEach(card => {
    const postId = card.getAttribute('data-comment-post-id');
    const commentTime = card.getAttribute('data-comment-time');
    if (!postId || !commentTime) return;

    const lastVisit = getPostVisitTime(postId);
    const commentTimestamp = parseInt(commentTime, 10) * 1000;

    // Unread if: never visited OR comment newer than last visit
    if (!lastVisit || commentTimestamp > lastVisit) {
      card.classList.add('unread-comment-card');
    }
  });
}

// Mobile: shrink header as the user scrolls.
function initShrinkHeader() {
  const header = document.querySelector('.site-header');
  const logo = header ? header.querySelector('.site-branding img') : null;
  if (!header || !logo) return;

  const NATURAL = logo.offsetHeight || 89;
  const MIN_LOGO = 23;
  const REST_HEADER = 80;       // 8rem
  const SHRUNK_HEADER = 40;     // 4rem
  const RANGE = 150;             // scroll px over which to fully shrink
  let ticking = false, attached = false;

  function apply() {
    ticking = false;
    const y = window.scrollY || window.pageYOffset || 0;
    const r = Math.min(1, Math.max(0, y / RANGE));
    logo.style.height = (NATURAL - (NATURAL - MIN_LOGO) * r) + 'px';
    header.style.minHeight = (REST_HEADER - (REST_HEADER - SHRUNK_HEADER) * r) + 'px';
  }
  function onScroll() {
    if (ticking) return;
    requestAnimationFrame(apply);
    ticking = true;
  }
  function enable() {
    if (attached) return;
    attached = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    apply();
  }
  function disable() {
    if (!attached) return;
    attached = false;
    window.removeEventListener('scroll', onScroll);
    header.style.minHeight = '';
    logo.style.height = '';
  }

  const mq = window.matchMedia('(max-width: 767.98px)');
  mq.addEventListener('change', e => e.matches ? enable() : disable());
  if (mq.matches) enable();
}

// Comment anchor id parsed from a URL hash ('#comment-123' -> 'comment-123'),
// null for anything else. Pure - self-checked in tests/comment-anchor.test.js
function commentAnchorId(hash) {
  const m = (hash || '').match(/^#(comment-\d+)$/);
  return m ? m[1] : null;
}

// Anchor navigation to a comment (#comment-N) lands wrong when the comment ad
// placeholders fill in late (Seznam SSP loads after the native hash scroll) and
// push the target comment down. Until the user interacts, re-anchor to the
// target whenever a placeholder resizes; stop once layout is stable.
function initCommentAnchorStabilizer() {
  const anchorId = commentAnchorId(window.location.hash);
  if (!anchorId || typeof ResizeObserver === 'undefined') return;
  const target = document.getElementById(anchorId);
  if (!target) return;
  const placeholders = document.querySelectorAll('.ad-placeholder-comment');
  if (placeholders.length === 0) return;

  let stopped = false;
  let settle = null;
  const ro = new ResizeObserver(() => {
    if (stopped) return;
    target.scrollIntoView({ block: 'start' }); // honors scroll-margin-top
    clearTimeout(settle);
    settle = setTimeout(stop, 2000); // layout stable for 2s -> done
  });
  function stop() {
    stopped = true;
    ro.disconnect();
    clearTimeout(settle);
  }
  // Any manual user input ends stabilization so we never fight the user.
  ['wheel', 'touchstart', 'keydown'].forEach(ev =>
    window.addEventListener(ev, stop, { once: true, passive: true })
  );
  placeholders.forEach(el => ro.observe(el));
  settle = setTimeout(stop, 15000); // hard cap
}

// If an ad never filled (ad blocker or SSP no-fill), drop the reserved
// placeholder height so comment lists don't show permanent blank gaps.
function collapseEmptyCommentAds() {
  document.querySelectorAll('.ad-placeholder-comment div[id^="ssp-zone-"]').forEach(div => {
    if (!div.firstChild) {
      const li = div.closest('.ad-placeholder-comment');
      if (li) li.classList.add('ad-collapsed');
    }
  });
}
window.addEventListener("message", (event) => {
  if (event?.data?.zoom) {
    const mains = document.getElementsByTagName('main')
    for (let main of mains) {
      main.style.zoom = event?.data?.zoom
    }
  }
}, false);

// ============================================================
// HOMEPAGE PAGINATION URL HELPERS (pure - self-checked in tests/pagination.test.js)
// ============================================================

// Homepage path for a page number: /page/N/ for N > 1, else the base path.
// /page/N/ is a real WordPress URL (home.php renders it server-side), so
// back-navigation after replaceState still shows the right posts.
function paginationPagePath(pathname, page) {
  const base = pathname.replace(/\/page\/\d+\/?$/, '/');
  return page > 1 ? base + 'page/' + page + '/' : base;
}

// Current page parsed from a homepage path, 1 when not paginated.
function paginationPageFromPath(pathname) {
  const m = pathname.match(/\/page\/(\d+)/);
  return m ? parseInt(m[1], 10) : 1;
}

document.addEventListener('DOMContentLoaded', () => {
  // Handle broken images in related posts (plugin strips the default
  // attachment-thumbnail class, so match any image in the list)
  document.querySelectorAll('ul.related_post li img').forEach(img => {
    img.addEventListener('error', function() {
      this.style.display = 'none';
    });
  });

  // document.body.style.overflowX = "hidden" // Disabled - was causing rigid Figma-like layout

  // Initialize map-related variables BEFORE they are used
  let currentMapCategory = '0';
  let mapLoaded = false;

  // Lazy load Geo Mashup map via AJAX
  async function loadMapWithCategory(categoryId = '0') {
    const mapContainer = document.getElementById('geo-map-container');
    if (!mapContainer) return;

    // Show loading overlay - this will be positioned over the map content
    mapContainer.classList.add('loading');
    
    // Remove any existing loading overlay
    const existingOverlay = mapContainer.querySelector('.loading-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    
    // Create loading overlay element
    const loadingOverlay = document.createElement('div');
    loadingOverlay.className = 'loading-overlay';
    loadingOverlay.innerHTML = '<span>Načítání mapy...</span>';
    mapContainer.appendChild(loadingOverlay);

    // Build URL with category parameter
    const url = categoryId === '0' || !categoryId
      ? window.location.origin + '/wp-json/msstavby/v1/geo-map'
      : window.location.origin + '/wp-json/msstavby/v1/geo-map?category=' + categoryId;

    // Load map via AJAX
    return fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (data.hasOwnProperty('success') && data.success && data.html) {
          // Inject map HTML
          mapContainer.innerHTML = data.html;
          
          // Re-add the loading overlay on top of the map
          const overlay = document.createElement('div');
          overlay.className = 'loading-overlay';
          overlay.innerHTML = '<span>Načítání mapy...</span>';
          mapContainer.appendChild(overlay);
          
          // DEBUG: Log map loading events
          const DEBUG_MAP_LOADING = false;
          const logDebug = (event, data = {}) => {
            if (DEBUG_MAP_LOADING) {
              console.log(`[MAP DEBUG] ${event}`, data);
            }
          };
          
          // Function to check if map has actual content loaded
          const isMapContentLoaded = (gmMap) => {
            if (!gmMap) {
              logDebug('isMapContentLoaded - no gmMap');
              return false;
            }
            // Check for various indicators that map content is loaded:
            // - Canvas elements (Google Maps)
            // - iframe elements
            // - img elements (map tiles)
            // - div elements with content (markers, info windows)
            const hasCanvas = gmMap.querySelector('canvas');
            const hasIframe = gmMap.querySelector('iframe');
            const hasTiles = gmMap.querySelectorAll('img').length;
            const hasMarkers = gmMap.querySelectorAll('div[style*="position"]').length;
            
            // Check iframe load state if exists
            let iframeLoaded = false;
            let iframeSrc = '';
            let iframeContentDoc = null;
            if (hasIframe) {
              iframeSrc = hasIframe.src || '';
              try {
                iframeContentDoc = hasIframe.contentDocument || hasIframe.contentWindow?.document;
                iframeLoaded = hasIframe.complete && iframeContentDoc?.readyState === 'complete';
              } catch(e) {
                // Cross-origin iframe - can't access contentDocument
                iframeLoaded = hasIframe.complete;
              }
              logDebug('iframe details', {
                src: iframeSrc,
                complete: hasIframe.complete,
                readyState: iframeContentDoc?.readyState,
                iframeLoaded: iframeLoaded
              });
            }
            
            const result = {
              hasCanvas: !!hasCanvas,
              hasIframe: !!hasIframe,
              hasTiles: hasTiles,
              hasMarkers: hasMarkers,
              iframeLoaded: iframeLoaded,
              isLoaded: !!(hasCanvas || (hasIframe && iframeLoaded) || hasTiles > 0 || hasMarkers > 0)
            };
            logDebug('isMapContentLoaded check', result);
            return result.isLoaded;
          };
          
          // Function to remove our overlay and cleanup
          const hideOverlay = (reason = 'unknown') => {
            logDebug('HIDING OVERLAY', { reason });
            overlay.remove();
            mapContainer.classList.remove('loading');
          };
          
          // Watch for removal of Geo Mashup's loading icon - it's removed when map is fully loaded
          const watchForGeoMashupLoad = () => {
            const gmLoadingIcon = document.getElementById('gm-loading-icon');
            logDebug('watchForGeoMashupLoad - checking for #gm-loading-icon', { found: !!gmLoadingIcon });
            
            if (gmLoadingIcon) {
              logDebug('Watching for #gm-loading-icon removal');
              // Watch for removal of Geo Mashup's loading icon
              const loadingObserver = new MutationObserver((mutations, obs) => {
                const stillExists = document.getElementById('gm-loading-icon');
                logDebug('Mutation observed (body)', { 
                  gmLoadingIconExists: !!stillExists,
                  mutationCount: mutations.length 
                });
                if (!stillExists) {
                  logDebug('#gm-loading-icon REMOVED');
                  hideOverlay('gm-loading-icon removed');
                  obs.disconnect();
                }
              });
              loadingObserver.observe(document.body, {
                childList: true,
                subtree: true
              });
              
              // Fallback timeout
              setTimeout(() => {
                loadingObserver.disconnect();
                logDebug('Fallback timeout - 20s reached');
                if (mapContainer.contains(overlay)) {
                  hideOverlay('fallback timeout');
                }
              }, 20000);
            } else {
              logDebug('No #gm-loading-icon found, checking content');
              // No Geo Mashup loading icon found, fall back to content check
              const gmMap = mapContainer.querySelector('.gm-map');
              if (gmMap && isMapContentLoaded(gmMap)) {
                hideOverlay('content loaded (no gm-loading-icon)');
              }
            }
          };
          
          // Check if .gm-map already exists and has content
          const existingGmMap = mapContainer.querySelector('.gm-map');
          logDebug('Checking for existing .gm-map', { found: !!existingGmMap });
          
          if (existingGmMap) {
            // .gm-map exists - check if iframe needs to load
            const iframe = existingGmMap.querySelector('iframe');
            if (iframe) {
              const iframeSrc = iframe.src || '';
              logDebug('Found iframe in existing .gm-map', { src: iframeSrc });
              
              // Try to check iframe load state
              try {
                const contentDoc = iframe.contentDocument || iframe.contentWindow?.document;
                logDebug('Iframe contentDocument state', { 
                  readyState: contentDoc?.readyState,
                  hasContent: !!contentDoc?.body?.innerHTML
                });
              } catch(e) {
                logDebug('Cannot access iframe contentDocument (cross-origin)', { error: e.message });
              }
              
              // Listen for iframe load event
              if (iframe.readyState !== 'complete') {
                logDebug('Iframe not loaded yet, adding load listener');
                iframe.addEventListener('load', function onIframeLoad() {
                  logDebug('IFRAME LOAD EVENT FIRED');
                  iframe.removeEventListener('load', onIframeLoad);
                  hideOverlay('iframe loaded');
                });
              } else {
                logDebug('Iframe already loaded (readyState=complete)');
                hideOverlay('iframe already loaded');
              }
            } else {
              logDebug('No iframe in .gm-map, checking for other content');
              if (isMapContentLoaded(existingGmMap)) {
                hideOverlay('existing gm-map with content (no iframe)');
              } else {
                logDebug('Waiting for map content to appear');
                // Watch for content to appear
                const contentObserver = new MutationObserver((mutations, obs) => {
                  logDebug('Content mutation observed', { mutationCount: mutations.length });
                  if (isMapContentLoaded(existingGmMap)) {
                    hideOverlay('content appeared after observation');
                    obs.disconnect();
                  }
                });
                contentObserver.observe(existingGmMap, {
                  childList: true,
                  subtree: true
                });
                setTimeout(() => {
                  contentObserver.disconnect();
                  if (mapContainer.contains(overlay)) {
                    hideOverlay('fallback timeout - content observation');
                  }
                }, 20000);
              }
            }
          } else {
            logDebug('Waiting for .gm-map to be created');
            // First wait for .gm-map to be created
            const observer = new MutationObserver((mutations, obs) => {
              const gmMap = mapContainer.querySelector('.gm-map');
              logDebug('Mutation observed (mapContainer)', { 
                gmMapFound: !!gmMap,
                mutationCount: mutations.length,
                addedNodes: mutations.map(m => m.addedNodes.length)
              });
              if (gmMap) {
                logDebug('.gm-map CREATED');
                obs.disconnect();
                // Now check for iframe and watch for Geo Mashup to finish loading
                const iframe = gmMap.querySelector('iframe');
                if (iframe) {
                  logDebug('Found iframe in newly created .gm-map', { src: iframe.src });
                  iframe.addEventListener('load', function onIframeLoad() {
                    logDebug('IFRAME LOAD EVENT FIRED (after .gm-map created)');
                    iframe.removeEventListener('load', onIframeLoad);
                    hideOverlay('iframe loaded (after gm-map created)');
                  });
                } else {
                  watchForGeoMashupLoad();
                }
              }
            });
            
            observer.observe(mapContainer, {
              childList: true,
              subtree: true
            });
            
            // Fallback timeout in case observer never triggers
            setTimeout(() => {
              observer.disconnect();
              logDebug('Fallback timeout - no .gm-map created');
              if (mapContainer.contains(overlay)) {
                hideOverlay('fallback timeout - no gm-map');
              }
            }, 20000);
          }
        } else if (data.hasOwnProperty('code') && data.code === 'plugin_missing') {
          loadingOverlay.remove();
          mapContainer.classList.remove('loading');
          mapContainer.innerHTML = '<div class="error">' + data.message + '</div>';
        }
      })
      .catch(error => {
        console.error('Error loading map:', error);
        loadingOverlay.remove();
        mapContainer.classList.remove('loading');
        mapContainer.innerHTML = '<div class="error">Chyba při načítání mapy</div>';
      });
  }

  // Load map with current category filter
  function lazyLoadGeoMap() {
    if (!mapLoaded) {
      const mapContainer = document.getElementById('geo-map-container');
      if (mapContainer && mapContainer.getAttribute('data-lazy-load') === 'true') {
	const mapObserver = new IntersectionObserver((entries, observer) => {
	  entries.forEach(entry => {
	    if (entry.isIntersecting) {
	      loadMapWithCategory(currentMapCategory).then(() => {
		mapLoaded = true;
	      });
	      observer.unobserve(entry.target);
	    }
	  });
	}, {
	  rootMargin: '200px 0px',
	  threshold: 0.1
	});

	mapObserver.observe(mapContainer);
      }
    }
  }

  // Map filter change handler
  const mapFilterRadios = document.querySelectorAll('input[type="radio"][name="map-filter"]');
  const mapFilterCheckboxes = document.querySelectorAll('input[type="checkbox"][name="map-filter"]');

  // Handle radio buttons (single selection mode)
  mapFilterRadios.forEach(radio => {
    radio.addEventListener('change', function() {
      if (this.checked) {
	const value = this.value;
	currentMapCategory = value;
	// Uncheck all checkboxes
	mapFilterCheckboxes.forEach(cb => cb.checked = false);
	// Reload the map immediately if already loaded
	if (mapLoaded) {
	  loadMapWithCategory(value);
	}
      }
    });
  });

  // Handle checkboxes (single selection mode)
  mapFilterCheckboxes.forEach(checkbox => {
    checkbox.addEventListener('change', function() {
      if (this.checked) {
        // Uncheck all other checkboxes and the radio button
        mapFilterCheckboxes.forEach(cb => {
          if (cb !== this) cb.checked = false;
        });
        mapFilterRadios.forEach(radio => radio.checked = false);
        
        currentMapCategory = this.value;
        
        // Reload the map immediately if already loaded
        if (mapLoaded) {
          loadMapWithCategory(currentMapCategory);
        }
      } else {
        // If unchecked, check the "all" radio button
        mapFilterRadios.forEach(radio => {
          if (radio.value === '0') radio.checked = true;
        });
        currentMapCategory = '0';
        
        if (mapLoaded) {
          loadMapWithCategory('0');
        }
      }
    });
  });

  lazyLoadGeoMap();

  // ============================================================
  // UNREAD COMMENT TRACKING
  // ============================================================

  // Initialize post visit tracking on single posts
  initPostVisitTracking();

  // Mark unread comments on single post pages
  indicateUnreadComments();

  // Mark unread comments on homepage latest comments section
  indicateUnreadLatestComments();

  // Mark unread comments on comments page (page-comments.php)
  indicateUnreadCommentsPage();

  // Mobile: shrink header on scroll
  initShrinkHeader();

  // Keep hash-navigated comments anchored while late ad layout settles
  initCommentAnchorStabilizer();

  // Ad blockers / no-fill: collapse comment ad placeholders that stay empty
  setTimeout(collapseEmptyCommentAds, 15000);

  // ============================================================
  // MAP LINK CLICK TRACKING
  // ============================================================

  // Track map page link clicks
  document.querySelectorAll('a[href*="/mapa/"]').forEach(link => {
    link.addEventListener('click', function() {
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({
          'event': 'link',
          'link': 'map'
        });
      }
    });
  });

  // Latest comments: clicking the article title opens the post itself,
  // not the comment anchor (the title is the only exception in the card).
  // The card is a single <a>, so the title can't be a nested anchor.
  document.addEventListener('click', function(e) {
    const title = e.target.closest('.latest-comments-section .comment-article-title[data-post-url]');
    if (!title) return;
    e.preventDefault();
    window.location.href = title.getAttribute('data-post-url');
  });

  // Search form toggle (vanilla, replaces jQuery fadeToggle; fade via Web Animations API)
  const searchButton = document.getElementById('searchbutt');
  const searchWrap = document.getElementById('searchWrap');
  if (searchButton && searchWrap) {
    searchButton.addEventListener('click', function (e) {
      e.preventDefault();
      const isOpen = searchWrap.classList.toggle('is-open');
      searchWrap.animate(
        [{ opacity: isOpen ? 0 : 1 }, { opacity: isOpen ? 1 : 0 }],
        { duration: 600, easing: 'linear' }
      );
    });
  }

  // Click-to-reply functionality for comments
  document.querySelectorAll('.comment-author-reply').forEach(function (author) {
    author.addEventListener('click', function () {
      const commentLink = author.getAttribute('data-comment-link');
      const authorName = author.getAttribute('data-comment-author');
      const commentID = author.getAttribute('data-comment-id');
      const textarea = document.querySelector('textarea#comment');

      if (textarea) {
        // Create @mention format
        const replyPrefix = '<a href="' + commentLink + '">@' + authorName + '</a>: ';
        const currentContent = textarea.value;

        // Add reply prefix to beginning of textarea
        if (currentContent.length > 0) {
          textarea.value = replyPrefix + currentContent;
        } else {
          textarea.value = replyPrefix;
        }

        // Focus the textarea
        textarea.focus();

        // Scroll to comment form
        const respond = document.getElementById('respond');
        if (respond) {
          window.scrollTo({
            top: respond.getBoundingClientRect().top + window.scrollY - 50,
            behavior: 'smooth'
          });
        }

        // Track reply event (DataLayer)
        if (typeof dataLayer !== 'undefined') {
          dataLayer.push({
            'event': 'reply',
            'reply': authorName,
            'comment_id': commentID
          });
        }
      }
    });
  });

  // ============================================================
  // COMMENT EDIT/DELETE FUNCTIONALITY
  // ============================================================

  // Update edit timers
  function updateEditTimers() {
    document.querySelectorAll('.edit-timer').forEach(timer => {
      const expires = parseInt(timer.getAttribute('data-expires'), 10);
      const remaining = Math.max(0, expires - Math.floor(Date.now() / 1000));
      
      if (remaining <= 0) {
        // Time expired - remove edit/delete buttons
        const commentItem = timer.closest('li[id^="comment-"]');
        if (commentItem) {
          const editBtn = commentItem.querySelector('.comment-edit-btn');
          const deleteBtn = commentItem.querySelector('.comment-delete-btn');
          if (editBtn) editBtn.remove();
          if (deleteBtn) deleteBtn.remove();
        }
      } else {
        // Show remaining time in minutes:seconds
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        timer.textContent = mins + ':' + (secs < 10 ? '0' : '') + secs;
      }
    });
  }

  // Update timers every second
  setInterval(updateEditTimers, 1000);
  updateEditTimers(); // Initial update

  // Handle edit button click
  document.addEventListener('click', async function(e) {
    const editBtn = e.target.closest('.comment-edit-btn');
    if (!editBtn) return;

    e.preventDefault();
    const commentId = editBtn.getAttribute('data-comment-id');
    const commentItem = document.getElementById('comment-' + commentId);
    if (!commentItem) return;

    const commentTextDiv = commentItem.querySelector('.comment-text');
    if (!commentTextDiv) return;

    // Check if already in edit mode
    if (commentTextDiv.querySelector('.comment-edit-textarea')) return;

    // Verify with server that comment is still editable (someone else may have commented)
    try {
      // Include WordPress nonce for authentication
      const nonce = (typeof msstavbyData !== 'undefined' && msstavbyData.nonce) ? msstavbyData.nonce : '';
      const headers = {};
      if (nonce) {
        headers['X-WP-Nonce'] = nonce;
      }
      
      const checkResponse = await fetch('/wp-json/msstavby/v1/comment/check/' + commentId, { headers });
      const checkData = await checkResponse.json();
      
      if (!checkData.can_edit) {
        if (checkData.reason === 'not_last_comment') {
          alert('Aktuálně není váš komentář poslední a už s ním není možno manipulovat.');
          window.location.reload();
        } else if (checkData.reason === 'time_expired') {
          alert('Časový limit pro úpravu komentáře vypršel.');
          window.location.reload();
        } else {
          alert('Komentář nelze upravit.');
        }
        return;
      }
    } catch (error) {
      console.error('Error checking comment editability:', error);
      // Continue anyway - the save will fail if not editable
    }

    // Get the raw comment content from data attribute (no <p> tags added by WordPress)
    const rawContent = commentItem.getAttribute('data-raw-content') || '';
    
    // Extract @mention prefix if present (e.g., <a href="...">@Name</a>: )
    // The @mention is the first link in the comment, followed by ": "
    let mentionPrefix = '';
    let editText = rawContent;
    
    const mentionMatch = rawContent.match(/^(<a[^>]*href="[^"]*#comment-[^"]*"[^>]*>@[^<]+<\/a>):\s*/);
    if (mentionMatch) {
      mentionPrefix = mentionMatch[0]; // <a href="...">@Name</a>: 
      editText = rawContent.substring(mentionMatch[0].length);
    }
    
    // Create edit form
    // Show the raw content so users can edit images, links, etc.
    const textarea = document.createElement('textarea');
    textarea.className = 'comment-edit-textarea';
    textarea.value = editText.trim();
    textarea.rows = 6;
    
    // Store the mention prefix for reconstruction on save
    textarea.setAttribute('data-mention-prefix', mentionPrefix);

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'comment-save-btn';
    saveBtn.textContent = 'Uložit';
    saveBtn.setAttribute('data-comment-id', commentId);

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'comment-cancel-btn';
    cancelBtn.textContent = 'Zrušit';

    const editContainer = document.createElement('div');
    editContainer.className = 'comment-edit-container';
    editContainer.appendChild(textarea);
    
    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'comment-edit-buttons';
    buttonContainer.appendChild(cancelBtn);
    buttonContainer.appendChild(saveBtn);
    editContainer.appendChild(buttonContainer);

    // Store original content and show edit form
    // Store the displayed HTML (with <p> tags) for cancel functionality
    commentTextDiv.setAttribute('data-original-content', commentTextDiv.innerHTML);
    commentTextDiv.innerHTML = '';
    commentTextDiv.appendChild(editContainer);
    textarea.focus();
  });

  // Handle save button click
  document.addEventListener('click', async function(e) {
    const saveBtn = e.target.closest('.comment-save-btn');
    if (!saveBtn) return;

    e.preventDefault();
    const commentId = saveBtn.getAttribute('data-comment-id');
    const commentItem = document.getElementById('comment-' + commentId);
    if (!commentItem) return;

    const textarea = commentItem.querySelector('.comment-edit-textarea');
    if (!textarea) return;

    let newContent = textarea.value.trim();
    if (!newContent) {
      alert('Komentář nemůže být prázdný.');
      return;
    }
    
    // Prepend the @mention prefix if it exists (so we preserve the reply structure)
    const mentionPrefix = textarea.getAttribute('data-mention-prefix') || '';
    if (mentionPrefix) {
      newContent = mentionPrefix + newContent;
    }

    // Store reference to buttons before any DOM changes
    const editBtn = commentItem.querySelector('.comment-edit-btn');
    const deleteBtn = commentItem.querySelector('.comment-delete-btn');

    saveBtn.disabled = true;
    saveBtn.textContent = 'Ukládání...';

    // Include WordPress nonce for authentication
    const nonce = (typeof msstavbyData !== 'undefined' && msstavbyData.nonce) ? msstavbyData.nonce : '';
    const headers = {
      'Content-Type': 'application/json',
    };
    if (nonce) {
      headers['X-WP-Nonce'] = nonce;
    }

    try {
      const response = await fetch('/wp-json/msstavby/v1/comment/edit/' + commentId, {
        method: 'POST',
        headers,
        body: JSON.stringify({ content: newContent }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update comment text with new content
        const commentTextDiv = commentItem.querySelector('.comment-text');
        if (commentTextDiv) {
          // Clear the stored original content since we've saved successfully
          commentTextDiv.removeAttribute('data-original-content');
          commentTextDiv.innerHTML = data.content;
        }
        // Update the raw content data attribute for future edits
        if (data.raw_content) {
          commentItem.setAttribute('data-raw-content', data.raw_content);
        }
        // Buttons are in .comment-actions, separate from .comment-text
        // They should still be visible - just verify they exist
        if (!editBtn || !deleteBtn) {
          console.warn('Edit/delete buttons missing after save - this should not happen');
        }
      } else {
        // Restore original content on error
        const commentTextDiv = commentItem.querySelector('.comment-text');
        if (commentTextDiv) {
          const original = commentTextDiv.getAttribute('data-original-content');
          if (original) {
            commentTextDiv.innerHTML = original;
          }
        }
        // Handle specific error cases
        if (data.code === 'cannot_edit' && data.message === 'not_last_comment') {
          alert('Aktuálně není váš komentář poslední a už s ním není možno manipulovat.');
          window.location.reload();
          return;
        }
        alert(data.message || 'Nepodařilo se uložit komentář.');
        // Re-enable save button on error
        saveBtn.disabled = false;
        saveBtn.textContent = 'Uložit';
      }
    } catch (error) {
      console.error('Error saving comment:', error);
      const commentTextDiv = commentItem.querySelector('.comment-text');
      if (commentTextDiv) {
        const original = commentTextDiv.getAttribute('data-original-content');
        if (original) {
          commentTextDiv.innerHTML = original;
        }
      }
      alert('Chyba při ukládání komentáře.');
      // Re-enable save button on error
      saveBtn.disabled = false;
      saveBtn.textContent = 'Uložit';
    }
  });

  // Handle cancel button click
  document.addEventListener('click', function(e) {
    const cancelBtn = e.target.closest('.comment-cancel-btn');
    if (!cancelBtn) return;

    e.preventDefault();
    const commentTextDiv = cancelBtn.closest('.comment-text');
    if (!commentTextDiv) return;

    // Restore original content
    const original = commentTextDiv.getAttribute('data-original-content');
    if (original) {
      commentTextDiv.innerHTML = original;
    }
  });

  // Handle delete button click
  document.addEventListener('click', async function(e) {
    const deleteBtn = e.target.closest('.comment-delete-btn');
    if (!deleteBtn) return;

    e.preventDefault();
    const commentId = deleteBtn.getAttribute('data-comment-id');

    // Verify with server that comment is still editable (someone else may have commented)
    // Include WordPress nonce for authentication
    const nonce = (typeof msstavbyData !== 'undefined' && msstavbyData.nonce) ? msstavbyData.nonce : '';
    const headers = {};
    if (nonce) {
      headers['X-WP-Nonce'] = nonce;
    }
    
    try {
      const checkResponse = await fetch('/wp-json/msstavby/v1/comment/check/' + commentId, { headers });
      const checkData = await checkResponse.json();
      
      if (!checkData.can_edit) {
        if (checkData.reason === 'not_last_comment') {
          alert('Aktuálně není váš komentář poslední a už s ním není možno manipulovat.');
          window.location.reload();
        } else if (checkData.reason === 'time_expired') {
          alert('Časový limit pro úpravu komentáře vypršel.');
          window.location.reload();
        } else {
          alert('Komentář nelze smazat.');
        }
        return;
      }
    } catch (error) {
      console.error('Error checking comment editability:', error);
      // Continue anyway - the delete will fail if not editable
    }

    // Confirm deletion
    if (!confirm('Opravdu chcete smazat tento komentář?')) return;

    deleteBtn.disabled = true;

    const deleteHeaders = {
      'Content-Type': 'application/json',
    };
    if (nonce) {
      deleteHeaders['X-WP-Nonce'] = nonce;
    }

    try {
      const response = await fetch('/wp-json/msstavby/v1/comment/delete/' + commentId, {
        method: 'POST',
        headers: deleteHeaders,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Remove comment from DOM with fade effect
        const commentItem = document.getElementById('comment-' + commentId);
        if (commentItem) {
          commentItem.style.transition = 'opacity 0.3s ease';
          commentItem.style.opacity = '0';
          setTimeout(() => {
            commentItem.remove();
          }, 300);
        }
      } else {
        deleteBtn.disabled = false;
        // Handle specific error cases
        if (data.code === 'cannot_delete' && data.message === 'not_last_comment') {
          alert('Aktuálně není váš komentář poslední a už s ním není možno manipulovat.');
          window.location.reload();
          return;
        }
        alert(data.message || 'Nepodařilo se smazat komentář.');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      deleteBtn.disabled = false;
      alert('Chyba při mazání komentáře.');
    }
  });

  // Categories Filter Functionality (chip buttons) - Only on homepage
  const bodyElement = document.body;
  const isHomePage = bodyElement.classList.contains('home') || window.location.pathname === '/';
  
  const filterChips = document.querySelectorAll('.filter-chip');
  
  if (!isHomePage) {
    return;
  }
  
  // Filter functionality only runs on homepage with filter chips
  if (filterChips.length === 0) {
    return;
  }
  
  // Only initialize filter variables if on homepage
  let selectedCategories = new Set();
  let allSelected = false;
  let currentPage = 1;
  const postsPerPage = 12;
  let totalPostsLoaded = 0;
  let midPageAdCounter = 0;

  // Mid-page ad zones - sourced from ads-config.php via window.msstavbyData.adZones
  const msstavbyAdZones = (window.msstavbyData && window.msstavbyData.adZones) || {};
  const midPageAdZone = msstavbyAdZones.load_more_1 || { desktop: 408699, mobile: 408729 };

  // Inject ad after the 6th post in a container
  function injectMidPageAd(container) {
    const row = container.querySelector('.row') || container;
    const cols = row.querySelectorAll(':scope > .col-12.col-md-4');
    if (cols.length > 6) {
      midPageAdCounter++;
      const adId = 'ssp-mid-page-' + midPageAdCounter;
      const isMobile = window.innerWidth <= 767;
      const zoneId = isMobile ? midPageAdZone.mobile : midPageAdZone.desktop;

      const adBlock = document.createElement('div');
      adBlock.className = 'col-12 mb-4';
      adBlock.innerHTML = '<div id="' + adId + '"></div>';
      cols[5].after(adBlock);

      if (window.sssp) {
        sssp.getAds([{
          zoneId: zoneId,
          id: adId,
          width: isMobile ? 480 : 970,
          height: isMobile ? 480 : 310
        }]);
      }
    }
  }

  // LocalStorage key for remembering filter state
  const STORAGE_KEY = 'msstavby_filter_state';

  function saveFilterState() {
    const state = {
      allSelected: allSelected,
      selectedCategories: Array.from(selectedCategories)
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  function loadFilterState() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const state = JSON.parse(stored);
        return state;
      } catch (e) {
        console.error('Error loading filter state:', e);
        return null;
      }
    }
    return null;
  }

  // Mirror the filter state into ?filter=slug[,slug...] on the homepage URL
  // (slugs come from the filter chips' data-slug attribute).
  // Unlike ?cat=X (which WordPress canonical-redirects to the category archive),
  // ?filter= is ignored by WordPress, so back-navigation stays on the homepage.
  function updateURLParams() {
    const params = new URLSearchParams(window.location.search);
    const slugs = allSelected ? [] : Array.from(selectedCategories)
      .map(catId => {
        const chip = document.querySelector(`.filter-chip[data-filter="${catId}"]`);
        return chip ? chip.getAttribute('data-slug') : null;
      })
      .filter(Boolean);

    if (slugs.length === 0) {
      params.delete('filter');
    } else {
      params.set('filter', slugs.join(','));
    }

    const search = params.toString();
    const path = paginationPagePath(window.location.pathname, currentPage);
    const newURL = search ? `${path}?${search}` : path;
    window.history.replaceState({}, '', newURL);
  }

  function applyFilterState(paramValue, page = 1) {
    let needsAjax = false;

    if (paramValue === null || paramValue === undefined) {
      // No URL param, load from localStorage
      const storedState = loadFilterState();
      if (storedState) {
        allSelected = storedState.allSelected;
        storedState.selectedCategories.forEach(catId => selectedCategories.add(catId));
        needsAjax = !allSelected && selectedCategories.size > 0;
      } else {
        // First visit (no URL, no localStorage): default UI shows "All",
        // and the server already rendered those posts. Skip the AJAX reload.
        allSelected = true;
        selectedCategories.clear();
      }
    } else if (paramValue === '0' || paramValue === '') {
      allSelected = true;
      selectedCategories.clear();
    } else {
      // Map slugs to category IDs via the filter chips
      const slugs = paramValue.toString().split(',').filter(Boolean);
      const catIds = slugs
        .map(slug => {
          const chip = document.querySelector(`.filter-chip[data-slug="${slug}"]`);
          return chip ? parseInt(chip.getAttribute('data-filter'), 10) : NaN;
        })
        .filter(id => !isNaN(id));
      if (catIds.length > 0) {
        allSelected = false;
        selectedCategories.clear();
        catIds.forEach(catId => selectedCategories.add(catId));
        needsAjax = true;
      } else {
        allSelected = true;
        selectedCategories.clear();
      }
    }

    setTimeout(() => {
      // Only fetch when a specific category is actually selected.
      // With "All" (or empty state) the server already rendered the posts — skip AJAX to avoid flicker.
      if (needsAjax) {
        filterPosts(page);
        if (page > 1) {
          setTimeout(() => {
            const heading = document.getElementById('latest-projects-heading');
            if (heading) heading.scrollIntoView({ block: 'start' });
          }, 400);
        }
      } else {
        // Initialize totalPostsLoaded from server-rendered content
        const projectsContainer = document.getElementById('projects');
        if (projectsContainer) {
          const row = projectsContainer.querySelector('.row') || projectsContainer;
          totalPostsLoaded = row.querySelectorAll(':scope > .col-12.col-md-4').length;
          if (page > 1) {
            currentPage = page;
            totalPostsLoaded = page * postsPerPage;
          }
        }
      }
      saveFilterState();
      updateURLParams();
      updateFilterChipUI();
    }, 100);
  }

  function updateFilterChipUI() {
    // Update chip classes based on current filter state
    filterChips.forEach(chip => {
      const filter = chip.getAttribute('data-filter');
      const chipElement = chip;

      chipElement.classList.remove('filter-chip-active');

      if (allSelected && filter === 'all') {
        chipElement.classList.add('filter-chip-active');
      } else if (!allSelected && filter !== 'all') {
        const catId = parseInt(filter);
        if (!isNaN(catId) && selectedCategories.has(catId)) {
          chipElement.classList.add('filter-chip-active');
        }
      }
    });
  }

  function filterPosts(paged = 1, append = false, restoreScroll = false, scrollPosition = 0) {
    currentPage = paged;
    const categoriesArray = allSelected ? [0] : Array.from(selectedCategories);

    // Show loading state
    const projectsContainer = document.getElementById('projects');
    if (projectsContainer) {
      // Store current height to prevent container collapse
      const containerHeight = projectsContainer.offsetHeight;
      // Add fade-out effect for transition
      projectsContainer.style.opacity = '0';
      projectsContainer.style.transition = 'opacity 0.15s ease-out';
      
      setTimeout(() => {
        projectsContainer.innerHTML = '<div class="col-12 text-center py-5"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Načítání...</span></div></div>';
        // Maintain height while loading to prevent page jump
        projectsContainer.style.minHeight = containerHeight + 'px';
        projectsContainer.style.opacity = '1';
      }, 150);
    }

    // Fetch filtered posts via AJAX
    fetch('/wp-json/msstavby/v1/filter-posts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        categories: categoriesArray,
        posts_per_page: 12,
        paged: currentPage,
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success && data.html) {
        if (projectsContainer) {
          if (append) {
            projectsContainer.insertAdjacentHTML('beforeend', data.html);
            // Track loaded posts for append case
            const row = projectsContainer.querySelector('.row') || projectsContainer;
            totalPostsLoaded = row.querySelectorAll(':scope > .col-12.col-md-4').length;
            loadMoreAdCounter = 1;
            // Show/hide load more button
            if (loadMoreButton) {
              if (data.total_posts && totalPostsLoaded < data.total_posts) {
                loadMoreButton.style.display = '';
                loadMoreButton.disabled = false;
                loadMoreButton.textContent = 'Další projekty';
              } else {
                loadMoreButton.style.display = 'none';
              }
            }
          } else {
            // Fade out before replacing content
            projectsContainer.style.opacity = '0';
            projectsContainer.style.transition = 'opacity 0.15s ease-out';
            
            setTimeout(() => {
              projectsContainer.innerHTML = data.html;
              // Inject mid-page ad after the 6th post
              injectMidPageAd(projectsContainer);
              if (restoreScroll) {
                window.scrollTo(0, scrollPosition);
              }
              // Remove the fixed height after content is loaded
              projectsContainer.style.minHeight = '';
              // Fade in
              projectsContainer.style.opacity = '1';
              
              totalPostsLoaded = currentPage * postsPerPage;
              loadMoreAdCounter = 1;
              // Show/hide load more button
              if (loadMoreButton) {
                if (data.total_posts && totalPostsLoaded < data.total_posts) {
                  loadMoreButton.style.display = '';
                  loadMoreButton.disabled = false;
                  loadMoreButton.textContent = 'Další projekty';
                } else {
                  loadMoreButton.style.display = 'none';
                }
              }
            }, 150);
          }
        }

        // Update
        const paginationContainer = document.getElementById('posts-pagination');
        const paginationRow = paginationContainer ? paginationContainer.closest('.row') : null;
        
        if (data.pagination) {
          if (paginationContainer) {
            paginationContainer.innerHTML = data.pagination;
            // Show the pagination row container
            if (paginationRow) {
              paginationRow.style.display = 'flex';
            }
          }
        } else {
          // Hide the pagination row container if no pagination
          if (paginationRow) {
            paginationRow.style.display = 'none';
          }
        }
      } else {
        if (projectsContainer) {
          // Fade out before showing empty message
          projectsContainer.style.opacity = '0';
          projectsContainer.style.transition = 'opacity 0.15s ease-out';
          
          setTimeout(() => {
            projectsContainer.innerHTML = '<div class="col-12 text-center py-5"><p class="text-muted">Žádné příspěvky nenalezeny.</p></div>';
            // Remove the fixed height
            projectsContainer.style.minHeight = '';
            // Fade in
            projectsContainer.style.opacity = '1';
          }, 150);
        }

        const paginationContainer = document.getElementById('posts-pagination');
        const paginationRow = paginationContainer ? paginationContainer.closest('.row') : null;
        
        if (paginationContainer) {
          paginationContainer.innerHTML = '';
        }
        if (paginationRow) {
          paginationRow.style.display = 'none';
        }
      }
    })
    .catch(error => {
      console.error('Error filtering posts:', error);
      if (projectsContainer) {
        // Fade out before showing error message
        projectsContainer.style.opacity = '0';
        projectsContainer.style.transition = 'opacity 0.15s ease-out';
        
        setTimeout(() => {
          projectsContainer.innerHTML = '<div class="col-12 text-center py-5"><p class="text-danger">Chyba při načítání příspěvků.</p></div>';
          // Remove the fixed height on error
          projectsContainer.style.minHeight = '';
          // Fade in
          projectsContainer.style.opacity = '1';
        }, 150);
      }
    });
  }

  // Pagination click handler (delegated)
  document.addEventListener('click', (e) => {
    const link = e.target && e.target.closest && e.target.closest('a.filter-page-link');
    if (!link) return;

    e.preventDefault();
    const page = parseInt(link.getAttribute('data-page'), 10);
    if (!Number.isFinite(page) || page < 1) return;

    // Load new page, sync the URL with it, and scroll to heading
    filterPosts(page, false, false, 0);
    updateURLParams();
    
    // Scroll to the heading after a short delay to allow content to load
    setTimeout(() => {
      const heading = document.getElementById('latest-projects-heading');
      if (heading) {
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  });

  // Load More button functionality - works like pagination but appends posts
  const loadMoreButton = document.getElementById('load-more-posts');
  let loadMoreAdCounter = 1;
  // Pre-batch load-more zones - sourced from ads-config.php
  const loadMoreAds = ['load_more_1']
    .map(function(key) { return msstavbyAdZones[key]; })
    .filter(Boolean);
  
  if (loadMoreButton) {
    loadMoreButton.addEventListener('click', function() {
      this.disabled = true;
      this.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Načítání...';
      
      // Get categories from current filter state
      const categoriesArray = allSelected ? [0] : Array.from(selectedCategories);
      
      // Calculate offset based on already loaded posts
      // Initial load is 12 posts, each load-more adds 12 more
      const offset = totalPostsLoaded;
      
      // Show loading state
      const projectsContainer = document.getElementById('projects');
      if (projectsContainer) {
        const row = projectsContainer.querySelector('.row') || projectsContainer;
        row.insertAdjacentHTML('beforeend', '<div class="col-12 text-center py-3 load-more-loading"><div class="spinner-border text-primary" role="status"><span class="visually-hidden">Načítání...</span></div></div>');
      }
      
      // Fetch next set of posts (12 posts per load-more: 6 + ad + 6)
      fetch('/wp-json/msstavby/v1/filter-posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          categories: categoriesArray,
          posts_per_page: 12,
          offset: offset,
        })
      })
      .then(response => response.json())
      .then(data => {
        // Remove loading indicator
        const loadingIndicators = projectsContainer.querySelectorAll('.load-more-loading');
        loadingIndicators.forEach(indicator => {
          indicator.remove();
        });
        
        if (data.success && data.html) {
          if (projectsContainer) {
            const row = projectsContainer.querySelector('.row') || projectsContainer;
            
            // Add ad block before the new set of posts
            loadMoreAdCounter++;
            if (loadMoreAdCounter <= loadMoreAds.length) {
              const adIndex = loadMoreAdCounter - 1;
              const zoneData = loadMoreAds[adIndex];
              const isMobile = window.innerWidth <= 767;
              const zoneId = isMobile ? zoneData.mobile : zoneData.desktop;
              const adId = 'ssp-load-more-' + loadMoreAdCounter;
              
              const adBlock = document.createElement('div');
              adBlock.className = 'col-12 mb-4';
              adBlock.innerHTML = '<div id="' + adId + '"></div>';
              row.appendChild(adBlock);
              
              if (window.sssp) {
                sssp.getAds([{
                  zoneId: zoneId,
                  id: adId,
                  width: isMobile ? 480 : 970,
                  height: isMobile ? 480 : 310
                }]);
              }
            }
            
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = data.html;
            
            // Extract post cards and append
            const cols = tempDiv.querySelectorAll('.col-12');
            let postCount = 0;
            cols.forEach((col, index) => {
              row.appendChild(col);
              postCount++;
              
              // Insert mid-ad after the 6th post in this batch
              if (postCount === 6) {
                midPageAdCounter++;
                const midAdId = 'ssp-mid-page-' + midPageAdCounter;
                const isMobile = window.innerWidth <= 767;
                const zoneId = isMobile ? midPageAdZone.mobile : midPageAdZone.desktop;
                
                const midAdBlock = document.createElement('div');
                midAdBlock.className = 'col-12 mb-4';
                midAdBlock.innerHTML = '<div id="' + midAdId + '"></div>';
                row.appendChild(midAdBlock);
                
                if (window.sssp) {
                  sssp.getAds([{
                    zoneId: zoneId,
                    id: midAdId,
                    width: isMobile ? 480 : 970,
                    height: isMobile ? 480 : 310
                  }]);
                }
              }
            });
            
            // Update total loaded count and sync the URL with the page
            // position (12 posts per page) so back-navigation restores it
            totalPostsLoaded += postCount;
            currentPage = Math.ceil(totalPostsLoaded / postsPerPage);
            updateURLParams();
          }
          
          // Update pagination UI to reflect current position
          const paginationContainer = document.getElementById('posts-pagination');
          if (paginationContainer && data.pagination) {
            paginationContainer.innerHTML = data.pagination;
          }
          
          // Show/hide load more button based on remaining posts
          if (data.total_posts && totalPostsLoaded < data.total_posts) {
            loadMoreButton.disabled = false;
            loadMoreButton.textContent = 'Další projekty';
          } else {
            loadMoreButton.style.display = 'none';
          }
        } else {
          loadMoreButton.style.display = 'none';
        }
      })
      .catch(error => {
        console.error('Error loading more posts:', error);
        loadMoreButton.disabled = false;
        loadMoreButton.textContent = 'Další projekty';
      });
    });
  }

  // Event listeners for filter chips
  filterChips.forEach(chip => {
    chip.addEventListener('click', function() {
      const filter = this.getAttribute('data-filter');

      // Any filter change resets to page 1
      currentPage = 1;
      
      if (filter === 'all') {
        // 'All' selected
        allSelected = true;
        selectedCategories.clear();
        
        // Update UI
        this.classList.add('filter-chip-active');
        filterChips.forEach(c => {
          if (c !== this) {
            c.classList.remove('filter-chip-active');
          }
        });
      } else {
        // Specific category selected
        const categoryId = parseInt(filter);
        allSelected = false;
        
        // Toggle selection
        if (selectedCategories.has(categoryId)) {
          selectedCategories.delete(categoryId);
          this.classList.remove('filter-chip-active');
          
          // If no categories selected, select 'all'
          if (selectedCategories.size === 0) {
            allSelected = true;
            const allChip = document.querySelector('[data-filter="all"]');
            if (allChip) allChip.classList.add('filter-chip-active');
          }
        } else {
          selectedCategories.add(categoryId);
          this.classList.add('filter-chip-active');
          
          // Uncheck 'all' chip
          const allChip = document.querySelector('[data-filter="all"]');
          if (allChip) {
            allChip.classList.remove('filter-chip-active');
          }
        }
      }
      
      filterPosts(1);
      saveFilterState();
      updateURLParams();
    });
  });

  // Apply filter from URL parameter (?filter=slug[,slug...]) or localStorage on
  // page load, and restore the pagination position from /page/N/ so the browser
  // back button returns to the page the user left.
  const urlParams = new URLSearchParams(window.location.search);
  applyFilterState(urlParams.get('filter'), paginationPageFromPath(window.location.pathname));
});
